import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  RefreshCw,
  Search,
  Clock,
  User,
  Phone,
  Calendar,
  Eye,
  PauseCircle,
  PlayCircle,
  X as CloseIcon,
  AlertCircle,
  RotateCcw,
} from 'lucide-react';
import { Card, Loading, Badge, Button } from '../../components/common';
import ResetConfirmationModal from '../../components/dashboard/ResetConfirmationModal/ResetConfirmationModal';
import conversationsService from '../../services/api/conversations.service';
import adminConversationsService from '../../services/api/admin.conversations.service';
import tenantsService from '../../services/api/tenants.service';
import useAuth from '../../hooks/useAuth';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Função helper para formatar datas com segurança
const formatSafeDate = (dateString) => {
  try {
    if (!dateString) return 'Data não disponível';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Data inválida';
    return formatDistanceToNow(date, {
      addSuffix: true,
      locale: ptBR,
    });
  } catch (error) {
    console.error('Erro ao formatar data:', dateString, error);
    return 'Data inválida';
  }
};

const Conversations = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.type === 'admin';
  
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('open');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [error, setError] = useState(null);
  const [conversationToReset, setConversationToReset] = useState(null);

  // Carregar tenants para admin
  useEffect(() => {
    if (isAdmin) {
      loadTenants();
    } else {
      loadConversations();
    }
  }, [isAdmin]);

  // Carregar conversas quando tenant mudar (não quando filtro mudar)
  useEffect(() => {
    if (selectedTenantId || !isAdmin) {
      loadConversations();
    }
  }, [selectedTenantId]); // ⚠️ REMOVIDO statusFilter daqui

  const loadTenants = async () => {
    try {
      const data = await tenantsService.getAll();
      setTenants(data);
      if (data.length > 0) {
        setSelectedTenantId(data[0].id);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar tenants:', error);
      setError('Erro ao carregar tenants: ' + error.message);
      setLoading(false);
    }
  };

  const loadConversations = async (reset = true) => {
    try {
      setLoading(true);
      setError(null);

      let tenantId;
      let needsAdminToken = false;
      
      if (isAdmin) {
        if (!selectedTenantId) {
          setConversations([]);
          setLoading(false);
          return;
        }
        tenantId = selectedTenantId;
        needsAdminToken = true;
      } else {
        tenantId = user?.tenantId || user?.id;
      }

      console.log('📊 Carregando conversas para tenant:', tenantId);

      if (needsAdminToken) {
        console.log('🔑 Admin detectado - gerando token temporário do tenant...');
        try {
          const tokenData = await tenantsService.generateToken(tenantId);
          localStorage.setItem('temp_tenant_token', tokenData.token);
          console.log('✅ Token temporário gerado');
        } catch (tokenError) {
          console.error('❌ Erro ao gerar token do tenant:', tokenError);
          throw new Error('Não foi possível autenticar como tenant');
        }
      }

      const params = {
        status: 'all', // ⚠️ SEMPRE busca TODAS as conversas
        limit: 100,
        cursor: reset ? null : nextCursor,
      };

      const data = await conversationsService.getConversations(tenantId, params);

      if (needsAdminToken) {
        localStorage.removeItem('temp_tenant_token');
      }

      const conversationsList = data.items || data.conversations || [];
      
      console.log('✅ Conversas carregadas:', conversationsList.length);

      if (reset) {
        setConversations(conversationsList);
      } else {
        setConversations((prev) => [...prev, ...conversationsList]);
      }

      setHasMore(!!data.nextCursor);
      setNextCursor(data.nextCursor);

    } catch (error) {
      console.error('❌ Erro ao carregar conversas:', error);
      setError(error.message || 'Erro ao carregar conversas');
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTenantChange = (tenantId) => {
    console.log('🔄 Mudando tenant para:', tenantId);
    setSelectedTenantId(tenantId);
    setConversations([]);
    setNextCursor(null);
    setHasMore(false);
  };

  const handleViewMessages = async (conversation) => {
    try {
      setLoadingMessages(true);
      setSelectedConversation(conversation);
      setMessages([]);
      
      let tenantId;
      let needsAdminToken = false;
      
      if (isAdmin) {
        tenantId = selectedTenantId;
        needsAdminToken = true;
      } else {
        tenantId = user?.tenantId || user?.id;
      }

      console.log('📨 Buscando mensagens para conversa:', conversation.id);

      if (needsAdminToken) {
        const tokenData = await tenantsService.generateToken(tenantId);
        localStorage.setItem('temp_tenant_token', tokenData.token);
      }

      const data = await conversationsService.getMessages(tenantId, conversation.id);
      
      if (needsAdminToken) {
        localStorage.removeItem('temp_tenant_token');
      }

      const messagesList = data.items || data.messages || [];
      
      setMessages(messagesList);
      console.log('✅ Mensagens carregadas:', messagesList.length);
      
    } catch (error) {
      console.error('❌ Erro ao carregar mensagens:', error);
      
      localStorage.removeItem('temp_tenant_token');
      
      setMessages([]);
      
      let errorMessage = 'Erro ao carregar mensagens';
      
      if (error.message === 'Network Error') {
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      } else if (error.message.includes('timeout')) {
        errorMessage = 'Tempo de espera excedido. Tente novamente.';
      } else if (error.message) {
        errorMessage = `Erro: ${error.message}`;
      }
      
      alert(errorMessage);
      
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleCloseConversation = async (conversation, e) => {
    if (e) e.stopPropagation();
    
    const reason = prompt('Digite o motivo do fechamento:');
    if (!reason) return;

    try {
      let tenantId;
      let needsAdminToken = false;
      
      if (isAdmin) {
        tenantId = selectedTenantId;
        needsAdminToken = true;
      } else {
        tenantId = user?.tenantId || user?.id;
      }

      if (needsAdminToken) {
        const tokenData = await tenantsService.generateToken(tenantId);
        localStorage.setItem('temp_tenant_token', tokenData.token);
      }

      await conversationsService.closeConversation(tenantId, conversation.id, { reason });
      
      if (needsAdminToken) {
        localStorage.removeItem('temp_tenant_token');
      }
      
      alert('Conversa fechada com sucesso!');
      await loadConversations();
      setSelectedConversation(null);
      
    } catch (error) {
      console.error('❌ Erro ao fechar conversa:', error);
      alert('Erro ao fechar conversa: ' + error.message);
    }
  };

  const handlePauseBot = async (conversation, e) => {
    if (e) e.stopPropagation();
    
    try {
      let tenantId;
      
      if (isAdmin) {
        tenantId = selectedTenantId;
      } else {
        tenantId = user?.tenantId || user?.id;
      }

      const waId = conversation.wa_id || conversation.lead?.phone;
      
      if (!waId) {
        alert('Número do WhatsApp não encontrado');
        return;
      }

      await conversationsService.pauseBot(waId, tenantId);
      
      alert('Bot pausado com sucesso!');
      await loadConversations();
      
    } catch (error) {
      console.error('❌ Erro ao pausar bot:', error);
      alert('Erro ao pausar bot: ' + error.message);
    }
  };

  const handleResumeBot = async (conversation, e) => {
    if (e) e.stopPropagation();
    
    try {
      let tenantId;
      
      if (isAdmin) {
        tenantId = selectedTenantId;
      } else {
        tenantId = user?.tenantId || user?.id;
      }

      const waId = conversation.wa_id || conversation.lead?.phone;
      
      if (!waId) {
        alert('Número do WhatsApp não encontrado');
        return;
      }

      await conversationsService.resumeBot(waId, tenantId);
      
      alert('Bot retomado com sucesso!');
      await loadConversations();
      
    } catch (error) {
      console.error('❌ Erro ao retomar bot:', error);
      alert('Erro ao retomar bot: ' + error.message);
    }
  };

  const handleResetConversation = async (conversation, e) => {
    if (e) e.stopPropagation();
    setConversationToReset(conversation);
  };

  const confirmResetConversation = async () => {
    if (!conversationToReset) return;

    try {
      console.log('🔄 Admin resetando conversa:', conversationToReset.id);

      await adminConversationsService.resetConversation(conversationToReset.id);
      
      alert('✅ Conversa resetada com sucesso!\n\nO fluxo foi reiniciado e o cliente receberá a mensagem inicial.');
      await loadConversations();
      
    } catch (error) {
      console.error('❌ Erro ao resetar conversa:', error);
      alert('Erro ao resetar conversa: ' + error.message);
    }
  };

  const getClientName = (conversation) => {
    return conversation.context?.nome || 
           conversation.context?.name ||
           conversation.lead?.name || 
           conversation.profile_name || 
           conversation.clientName ||
           'Cliente';
  };

  const getClientPhone = (conversation) => {
    return conversation.lead?.phone || 
           conversation.wa_id || 
           conversation.phone ||
           '';
  };

  const getConversationStatus = (conversation) => {
    let currentStep = '';
    
    if (conversation.context) {
      currentStep = conversation.context.current_step || 
                   conversation.context.currentStep || 
                   conversation.context.step ||
                   conversation.context.stepId ||
                   conversation.context.step_id || '';
    }
    
    if (!currentStep && conversation) {
      currentStep = conversation.current_step ||
                   conversation.currentStep ||
                   conversation.step || '';
    }
    
    const stepStr = String(currentStep).toLowerCase();
    
    if (stepStr.includes('perguntar_reinicio')) {
      return 'closed';
    }
    
    if (conversation.context?.flow_finished === true || 
        conversation.context?.flow_finished === 'true') {
      return 'closed';
    }
    
    if (conversation.context?.restarted_by_user === true || 
        conversation.context?.restarted_by_user === 'true') {
      return 'open';
    }
    
    return conversation.status || 'open';
  };

  const isAwaitingRestart = (conversation) => {
    let currentStep = '';
    
    if (conversation.context) {
      currentStep = conversation.context.current_step || 
                   conversation.context.currentStep || 
                   conversation.context.step ||
                   conversation.context.stepId ||
                   conversation.context.step_id || '';
    }
    
    if (!currentStep && conversation) {
      currentStep = conversation.current_step ||
                   conversation.currentStep ||
                   conversation.step || '';
    }
    
    const stepStr = String(currentStep).toLowerCase();
    
    if (stepStr.includes('perguntar_reinicio')) {
      return true;
    }
    
    return conversation.context?.awaiting_restart_response === true || 
           conversation.context?.awaiting_restart_response === 'true';
  };

  const filteredConversations = conversations.filter((conv) => {
    // Primeiro filtra por status usando nossa função
    const currentStatus = getConversationStatus(conv);
    
    // Aplica filtro de status
    if (statusFilter === 'open' && currentStatus !== 'open') {
      return false;
    }
    if (statusFilter === 'closed' && currentStatus !== 'closed') {
      return false;
    }
    // statusFilter === 'all' não precisa filtrar
    
    // Depois filtra por busca
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    const name = getClientName(conv).toLowerCase();
    const phone = getClientPhone(conv);
    const id = conv.id?.toLowerCase() || '';
    const protocol = conv.protocol?.toLowerCase() || '';
    
    return name.includes(search) || 
           phone.includes(search) || 
           id.includes(search) ||
           protocol.includes(search);
  });

  if (loading && conversations.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Loading size="lg" text="Carregando conversas..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageSquare className="w-7 h-7 text-primary-500" />
            Conversas
          </h1>
          <p className="text-gray-600 mt-1">
            {isAdmin 
              ? '👨‍💼 Visualização Admin - Gerencie conversas de todos os tenants' 
              : 'Gerencie e acompanhe suas conversas com clientes'}
          </p>
        </div>

        <button
          onClick={() => loadConversations(true)}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span className="text-sm font-medium">Atualizar</span>
        </button>
      </div>

      {isAdmin && tenants.length > 0 && (
        <Card>
          <Card.Body className="bg-blue-50 border border-blue-200">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-blue-900">
                Visualizar conversas de:
              </label>
              <select
                value={selectedTenantId || ''}
                onChange={(e) => handleTenantChange(e.target.value)}
                className="flex-1 px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-white"
              >
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name} ({tenant.id})
                  </option>
                ))}
              </select>
            </div>
          </Card.Body>
        </Card>
      )}

      {error && (
        <Card>
          <Card.Body className="bg-red-50 border border-red-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-red-900 mb-1">
                  Erro ao carregar conversas
                </h3>
                <p className="text-sm text-red-700">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadConversations(true)}
                  className="mt-3"
                >
                  Tentar Novamente
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Body>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome, telefone, protocolo ou ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setStatusFilter('open')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  statusFilter === 'open'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Abertas
              </button>
              <button
                onClick={() => setStatusFilter('closed')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  statusFilter === 'closed'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Fechadas
              </button>
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Todas
              </button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {!selectedTenantId && isAdmin ? (
        <Card>
          <Card.Body className="text-center py-12">
            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Selecione um Tenant
            </h3>
            <p className="text-gray-600">
              Escolha um tenant acima para visualizar suas conversas
            </p>
          </Card.Body>
        </Card>
      ) : filteredConversations.length === 0 && !loading ? (
        <Card>
          <Card.Body className="text-center py-12">
            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Nenhuma conversa encontrada
            </h3>
            <p className="text-gray-600 mb-4">
              {searchTerm
                ? 'Tente ajustar os filtros de busca'
                : statusFilter === 'open'
                ? 'Não há conversas abertas no momento'
                : statusFilter === 'closed'
                ? 'Não há conversas fechadas'
                : 'Não há conversas registradas'}
            </p>
          </Card.Body>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredConversations.map((conversation) => (
            <Card key={conversation.id} hoverable>
              <Card.Body>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-primary-600" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {getClientName(conversation)}
                        </h3>
                        <Badge
                          variant={getConversationStatus(conversation) === 'open' ? 'success' : 'secondary'}
                          size="sm"
                        >
                          {getConversationStatus(conversation) === 'open' ? 'Aberta' : 'Fechada'}
                        </Badge>
                        {conversation.bot_paused && (
                          <Badge variant="warning" size="sm">
                            Bot Pausado
                          </Badge>
                        )}
                        {isAwaitingRestart(conversation) && (
                          <Badge variant="primary" size="sm">
                            Aguardando Reinício
                          </Badge>
                        )}
                      </div>

                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Phone className="w-4 h-4" />
                          <span>{getClientPhone(conversation)}</span>
                        </div>
                        {conversation.protocol && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <span className="font-medium">Protocolo:</span>
                            <span>#{conversation.protocol}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span>Iniciada {formatSafeDate(conversation.created_at || conversation.createdAt)}</span>
                        </div>
                        {(conversation.updated_at || conversation.lastInboundAt) && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Clock className="w-4 h-4" />
                            <span>Última atualização {formatSafeDate(conversation.updated_at || conversation.lastInboundAt)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewMessages(conversation)}
                      leftIcon={<Eye className="w-4 h-4" />}
                    >
                      Ver
                    </Button>

                    {getConversationStatus(conversation) === 'open' && (
                      <>
                        {conversation.bot_paused ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => handleResumeBot(conversation, e)}
                            leftIcon={<PlayCircle className="w-4 h-4" />}>
                            Retomar
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => handlePauseBot(conversation, e)}
                            leftIcon={<PauseCircle className="w-4 h-4" />}
                          >
                            Pausar
                          </Button>
                        )}

                        <Button
                          variant="danger"
                          size="sm"
                          onClick={(e) => handleCloseConversation(conversation, e)}
                          leftIcon={<CloseIcon className="w-4 h-4" />}
                        >
                          Fechar
                        </Button>
                      </>
                    )}

                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => handleResetConversation(conversation, e)}
                        leftIcon={<RotateCcw className="w-4 h-4" />}
                        className="text-orange-600 hover:text-orange-700 hover:border-orange-600"
                        title="Resetar conversa e reiniciar fluxo"
                      >
                        Resetar
                      </Button>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}

      {selectedConversation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Mensagens - {getClientName(selectedConversation)}
                </h2>
                <p className="text-sm text-gray-600">{getClientPhone(selectedConversation)}</p>
              </div>
              <button
                onClick={() => setSelectedConversation(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <CloseIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {loadingMessages ? (
                <div className="flex items-center justify-center py-12">
                  <Loading size="md" text="Carregando mensagens..." />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  Nenhuma mensagem encontrada
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message, index) => {
                    const isOutbound = message.direction === 'outbound' || message.direction === 'bot';
                    
                    let messageText = '[Mensagem sem texto]';
                    
                    if (message.text && typeof message.text === 'string') {
                      messageText = message.text;
                    } else if (message.body) {
                      if (typeof message.body === 'string') {
                        messageText = message.body;
                      } else if (message.body.text && typeof message.body.text === 'string') {
                        messageText = message.body.text;
                      } else if (typeof message.body === 'object') {
                        if (message.body.type === 'image') {
                          messageText = '📷 Imagem';
                        } else if (message.body.type === 'video') {
                          messageText = '🎥 Vídeo';
                        } else if (message.body.type === 'audio') {
                          messageText = '🎵 Áudio';
                        } else if (message.body.type === 'document') {
                          messageText = '📄 Documento';
                        } else {
                          messageText = `[Mensagem do tipo: ${message.body.type || 'desconhecido'}]`;
                        }
                      }
                    }
                    
                    return (
                      <div
                        key={message.id || index}
                        className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg p-4 ${
                            isOutbound
                              ? 'bg-primary-500 text-white'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">
                            {messageText}
                          </p>
                          <p
                            className={`text-xs mt-2 ${
                              isOutbound ? 'text-primary-100' : 'text-gray-500'
                            }`}
                          >
                            {new Date(message.timestamp || message.createdAt || message.created_at).toLocaleString('pt-BR')}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {conversationToReset && (
        <ResetConfirmationModal
          conversation={conversationToReset}
          onConfirm={confirmResetConversation}
          onClose={() => setConversationToReset(null)}
        />
      )}
    </div>
  );
};

export default Conversations;